'use client';

import type { RuleClientPriority } from '@/app/models/client';
import { useRemoveClientPriorityMutation } from '@/app/services/clientPriorityApi';
import ActionMenu, { ItemAction } from '@/components/common/ActionMenu';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import CancelOrConfirmDialog from '@/components/common/dialogs/CancelOrConfirmDialog';
import { TableCell } from '@mui/material';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

type EventTableParams = {
  rows: RuleClientPriority[];
  setSelectedItem: (p: RuleClientPriority) => void;
  setDrawerOpen: (open: boolean) => void;
};

export default function ProjectPriorityTable(props: EventTableParams) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);

  const { t } = useTranslation(['projectPriority', 'common']);
  const { rows, setSelectedItem, setDrawerOpen } = props;

  const [deleteClientPriority] = useRemoveClientPriorityMutation();

  const handleDialogClose = () => {
    setDeleteDialog(false);
  };

  const handleDeleteItem = async () => {
    if (selectedId) {
      const response = await deleteClientPriority(selectedId);
      if ('data' in response) {
        toast.dismiss();
        toast.success(
          t('delete.client.priority.success', {
            position: toast.POSITION.TOP_RIGHT,
          }),
        );
      } else {
        toast.error(
          t('common:processing.failed', {
            position: toast.POSITION.TOP_RIGHT,
          }),
        );
      }
      handleDialogClose();
      setSelectedId(null);
    }
  };

  const columns: Column[] = [
    { key: 'client', label: t('client') },
    { key: 'priority', label: t('priority'), align: 'center' },
    { key: 'actions', label: t('common:actions'), align: 'right' },
  ];

  const renderRow = (row: RuleClientPriority) => (
    <>
      <TableCell sx={bodyCellSx}>{`${row.client.name}`}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>{`${row.priority}`}</TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
        <ActionMenu>
          <ItemAction
            text={t('common:edit') as string}
            actionType="edit"
            onClick={() => {
              setSelectedItem(row);
              setDrawerOpen(true);
            }}
          />
          <ItemAction
            text={t('common:delete') as string}
            actionType="delete"
            onClick={() => {
              setSelectedId(row.id);
              setDeleteDialog(true);
            }}
          />
        </ActionMenu>
      </TableCell>
    </>
  );

  // Use the row ID as the unique key
  const getRowKey = (row: RuleClientPriority) => row.id;

  const deleteDialogProps = {
    title: t('confirm.delete.title'),
    contentText: <Trans>{t('confirm.delete.message')}</Trans>,
  };

  return (
    <>
      <DataTable
        columns={columns}
        data={rows}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
        stickyHeader
        size="small"
      />

      <CancelOrConfirmDialog
        title={deleteDialogProps.title}
        contentText={deleteDialogProps.contentText}
        open={deleteDialog}
        onClose={() => handleDialogClose()}
        onConfirm={async () => handleDeleteItem()}
      />
    </>
  );
}
